import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

export const useAcceptInvitationStyles = makeStyles({
  root: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
  },
  topBar: {
    flexShrink: 0,
    height: '46px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },
  meControlButton: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    maxWidth: '280px',
  },
  meControlName: {
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  brandLogo: {
    height: '20px',
    width: 'auto',
    display: 'block',
    flexShrink: 0,
    // Left-align the logo in the column-layout error cards/dialogs (ADO 10987530). The row-layout
    // top bar restores vertical centering via topBarLogo.
    alignSelf: 'flex-start',
    objectFit: 'contain',
  },
  // Top-bar logo lives in a row flex; center it vertically (overrides brandLogo's flex-start) and
  // pin the MSALogo lockup to its intended 145x16 header size (overrides brandLogo's 20px height).
  topBarLogo: {
    alignSelf: 'center',
    width: '145px',
    height: '16px',
  },
  pageHeader: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },
  layout: {
    flexGrow: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: '232px minmax(0, 1fr)',
  },
  // Fix for ADO 10941326 (MAS 1.4.10 Reflow): applied (via useResponsiveSize) at the shared
  // narrow breakpoints (< 640px, i.e. displayMode 's'/'m', covering 320px) — drop the fixed
  // 232px side rail into a single stacked column so the form content is no longer squeezed/
  // obscured behind it and no horizontal scrolling is required.
  layoutNarrow: {
    // minmax(0, 1fr) lets the content column shrink below its min-content width so a wide child
    // can't force the layout wider than the viewport.
    gridTemplateColumns: 'minmax(0, 1fr)',
    // Fluent Dropdown/Combobox defaults to min-width: 250px, which is wider than the ~240px
    // available at 320px — allow it to shrink to the column width instead.
    '& .fui-Dropdown, & .fui-Combobox': {
      minWidth: 0,
      width: '100%',
      maxWidth: '100%',
    },
  },
  rail: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRight(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    paddingTop: tokens.spacingVerticalXL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalL,
  },
  // When stacked (see layoutNarrow) the rail becomes a top row: swap the right divider for a
  // bottom divider and tighten vertical padding.
  railNarrow: {
    borderRightWidth: 0,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
  },
  railStep: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase300,
  },
  railDot: {
    width: '12px',
    height: '12px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground,
  },
  content: {
    overflowY: 'auto',
    paddingTop: '28px',
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '88px',
  },
  titleBlock: {
    marginBottom: tokens.spacingVerticalXXXL,
  },
  formCard: {
    maxWidth: '720px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },
  cardHeaderIcon: {
    display: 'block',
    flexShrink: 0,
    fontSize: '20px',
    lineHeight: '20px',
    color: tokens.colorNeutralForeground1,
  },
  cardHeaderTitle: {
    margin: 0,
    fontWeight: tokens.fontWeightSemibold,
  },
  formBody: {
    display: 'grid',
    rowGap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    // Hide the leading icon in inline Field validation messages — show text only.
    '& .fui-Field__validationMessageIcon': {
      display: 'none',
    },
  },
  termsRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalM,
    ...shorthands.borderTop(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },
  fieldErrorText: {
    color: tokens.colorStatusDangerForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  preferredEmailLabel: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  preferredEmailDescription: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  requiredIndicator: {
    color: tokens.colorStatusDangerForeground1,
  },
  checkboxLabel: {
    fontWeight: tokens.fontWeightRegular,
  },
  marketingSection: {
    display: 'grid',
    rowGap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    ...shorthands.borderTop(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },
  contactBySection: {
    display: 'grid',
    rowGap: tokens.spacingVerticalXS,
    marginLeft: tokens.spacingHorizontalL,
  },
  footer: {
    position: 'fixed',
    left: '232px',
    right: 0,
    bottom: 0,
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderTop(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },
  // Fix for ADO 10941326 (MAS 1.4.10 Reflow): the rail is no longer beside the content when
  // stacked, so span the footer full width instead of offsetting it by the 232px rail.
  footerNarrow: {
    left: 0,
    paddingLeft: tokens.spacingHorizontalL,
  },
  alert: {
    maxWidth: '720px',
    marginBottom: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    color: tokens.colorStatusDangerForeground1,
    backgroundColor: tokens.colorStatusDangerBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorStatusDangerBackground2),
    borderRadius: tokens.borderRadiusMedium,
  },
  centeredPage: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    paddingTop: tokens.spacingVerticalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
  },
  errorCard: {
    width: '480px',
    maxWidth: 'calc(100vw - 48px)',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    textAlign: 'left',
  },
  errorTitle: {
    marginTop: tokens.spacingVerticalS,
  },
  errorBody: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground2,
  },
  // Actions row for error cards. Buttons keep their natural width and never shrink below their
  // label (flexShrink 0), so when both don't fit on one line the second wraps to the next line
  // (flexWrap). If a single button is still wider than the row (e.g. a long localized label), it's
  // capped to the row width and its label is truncated with an ellipsis instead of overflowing the
  // button border.
  errorActions: {
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalS,
    columnGap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
    '& > button': {
      flexShrink: 0,
      minWidth: 0,
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  // Plain full-viewport neutral background rendered behind the existing-profile / denied dialog so
  // the modal is not layered over the "Create your user profile" shell chrome (matches the design).
  dialogBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // Full-viewport neutral loading screen shown on top of the profile form while the Activate request
  // is in flight (the form stays mounted underneath so its entered values are preserved if Activate
  // fails and the form is shown again with the error). On success the whole page is replaced by the
  // confirmation view.
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    zIndex: 1,
  },
  // Widen/normalize the alert dialog and lay its content out as a single vertical column: brand mark,
  // title, body, then the actions on their own row (Fluent's default DialogBody grid otherwise places
  // a stray brand row next to the actions).
  dialogSurface: {
    width: '480px',
    maxWidth: 'calc(100vw - 48px)',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  dialogTitle: {
    marginTop: tokens.spacingVerticalXS,
  },
  dialogActions: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    rowGap: tokens.spacingVerticalS,
    columnGap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
    // Buttons keep their natural width and never shrink below their label (flexShrink 0), so when
    // both don't fit on one line the second wraps to the next line (flexWrap). A button that is
    // still wider than the row is capped to the row width and its label is truncated with an
    // ellipsis instead of overflowing the button border.
    '& > button': {
      flexShrink: 0,
      minWidth: 0,
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  confirmation: {
    width: '560px',
    maxWidth: 'calc(100vw - 48px)',
    textAlign: 'center',
  },
  confirmationText: {
    display: 'block',
    textAlign: 'center',
  },
  celebration: {
    width: '72px',
    height: '72px',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: tokens.spacingVerticalM,
    display: 'block',
  },
  summaryCard: {
    marginTop: tokens.spacingVerticalXXXL,
    marginBottom: tokens.spacingVerticalXXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
    textAlign: 'left',
  },
  summaryRow: {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    columnGap: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },
  summaryValue: {
    textAlign: 'right',
    fontWeight: tokens.fontWeightSemibold,
  },
  permissionValue: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    rowGap: tokens.spacingVerticalXXS,
    textAlign: 'right',
    fontWeight: tokens.fontWeightSemibold,
  },
  accessList: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    rowGap: tokens.spacingVerticalXXS,
    textAlign: 'right',
  },
  accessEntry: {
    fontWeight: tokens.fontWeightSemibold,
  },
  accessMore: {
    marginTop: tokens.spacingVerticalXXS,
  },
  dropdownListbox: {
    maxHeight: '240px',
    overflowY: 'auto',
  },
});
